import React, { Component } from 'react'
import { useHistory } from 'react-router-dom'

import {
  DiningIcon,
  RestaurantIcon,
  ServingTrayIcon
} from '@toasttab/buffet-pui-icons'
import PropTypes from 'prop-types'

import { DigitalSurface } from 'src/apollo/onlineOrdering'
import { Query } from 'src/lib/js/hooks/useQueryString'
import DropDown from 'src/shared/components/common/dropdown'
import { DropDownOption } from 'src/shared/components/common/dropdown/DropDownOption'
import { LocationSearchError } from 'src/shared/components/common/location_search/LocationSearchError'
import { LocationSearchErrorContextProvider } from 'src/shared/components/common/location_search/LocationSearchErrorContext'

import Image from 'shared/components/common/Image'
import { useFlags } from 'shared/components/common/feature_flags/useFlags'
import PlacesAutocomplete from 'shared/components/common/location_search/PlacesAutocomplete'

import { gMapsAPIKey } from 'config'
import { t } from 'src/translations/src'

class LocationInput extends Component<
  Props & { history: any; wrapperRef: React.RefObject<HTMLDivElement> },
  { editMode: boolean; locationError: string }
> {
  static propTypes = {
    history: PropTypes.object,
    wrapperRef: PropTypes.object,
    parsedQS: PropTypes.object,
    street: PropTypes.string,
    neighborhood: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    onLocationSelected: PropTypes.func
  }

  constructor(
    props: Props & { history: any; wrapperRef: React.RefObject<HTMLDivElement> }
  ) {
    super(props)
    this.state = { editMode: false, locationError: '' }
    this.onAddressSelected = this.onAddressSelected.bind(this)
    this.handleClickOutside = this.handleClickOutside.bind(this)
  }

  handleClickOutside(event: MouseEvent) {
    if (
      this.props.wrapperRef &&
      event.target &&
      !this.props.wrapperRef.current?.contains(event.target as Node)
    ) {
      this.setState((state) => ({ ...state, editMode: false }))
    }
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  onAddressSelected(location: any) {
    // fields used here must be specified in GMAPS_PLACERESULT_FIELDS
    if (location && location.geometry && location.geometry.location) {
      let street = ''
      let neighborhood = ''
      let city = ''
      let state = ''
      for (let component of location.address_components) {
        for (let componentType of component.types) {
          if (componentType === 'street_number') {
            street = street
              ? component.long_name + ' ' + street
              : component.long_name
            break
          } else if (componentType === 'route') {
            street = street
              ? street + ' ' + component.short_name
              : component.short_name
            break
          } else if (componentType === 'neighborhood') {
            neighborhood = component.long_name
            break
          } else if (componentType === 'locality') {
            city = component.long_name
            break
          } else if (componentType === 'administrative_area_level_1') {
            state = component.short_name
            break
          }
        }
      }
      const lat = location.geometry.location.lat()
      const long = location.geometry.location.lng()

      this.setState({ editMode: false, locationError: '' })
      this.props.onLocationSelected(
        street,
        neighborhood,
        city,
        state,
        lat,
        long
      )
    }
  }

  render() {
    const dropDownLabel =
      this.props.street ||
      this.props.neighborhood ||
      (this.props.city && this.props.state
        ? `${this.props.city}, ${this.props.state}`
        : this.props.city)

    return (
      <>
        <div
          className='location-dropdown-input-wrapper'
          ref={this.props.wrapperRef}
        >
          {this.state.editMode ? (
            <div className='address-input'>
              <PlacesAutocomplete
                id='rx-search-location-dropdown'
                useCurrentLocation
                placeholder={t('location-dropdown.enter-city')}
                showGenericSpinner
                apiKey={gMapsAPIKey}
                onPlaceSelected={this.onAddressSelected}
                source={DigitalSurface.Local}
              />
            </div>
          ) : (
            <div
              className='selected-location'
              onClick={() => this.setState({ editMode: true })}
            >
              <Image
                className='map-pin'
                src='toastLocal/icons/location-blue.svg'
                alt={t('location-dropdown.update-location')}
              />
              <div className='address'>
                {dropDownLabel || t('location-dropdown.enter-city')}
              </div>
              <Image
                className='caret'
                src='toastLocal/splash/caret-down-blue.svg'
                alt={t('location-dropdown.caret')}
              />
            </div>
          )}
        </div>
        {this.state.locationError ? (
          <p className='txt-body-sm error location-error'>
            {this.state.locationError}
          </p>
        ) : null}
      </>
    )
  }
}

export type DropdownSearchMode = 'Restaurants' | 'Catering' | 'Venues'

type Props = {
  mode?: DropdownSearchMode
  parsedQS: Query
  street: string
  neighborhood: string
  city: string
  state: string
  onDropdownChange: (mode: DropdownSearchMode) => void
  onLocationSelected: (
    street: string,
    neighborhood: string,
    city: string,
    state: string,
    lat: number,
    long: number
  ) => void
}

const LocationDropDown = ({
  mode,
  parsedQS,
  street,
  neighborhood,
  city,
  state,
  onLocationSelected,
  onDropdownChange
}: Props) => {
  const history = useHistory()
  const wrapperRef = React.createRef<HTMLDivElement>()

  const { nvsCateringDiscoveryToastLocal, nvsMarketplaceLeadForms } = useFlags()

  const optionLabels: Record<DropdownSearchMode, JSX.Element> = {
    Restaurants: (
      <div className='location-dropdown-select-mode'>
        <RestaurantIcon />
        {t('location-dropdown.restaurants')}
      </div>
    ),
    Catering: (
      <div className='location-dropdown-select-mode'>
        <ServingTrayIcon />
        {t('location-dropdown.catering')}
      </div>
    ),
    Venues: (
      <div className='location-dropdown-select-mode'>
        <DiningIcon />
        {t('location-dropdown.venues')}
      </div>
    )
  }

  const label = mode ? optionLabels[mode] : optionLabels.Restaurants

  return (
    <LocationSearchErrorContextProvider>
      <div className='location-dropdown-container'>
        <div className='location-dropdown-pill' ref={wrapperRef}>
          {nvsCateringDiscoveryToastLocal && (
            <>
              <DropDown testId='location-search-mode-dropdown' label={label}>
                {({ close }) => (
                  <>
                    <DropDownOption
                      onSelect={() => {
                        if (mode !== 'Restaurants')
                          onDropdownChange('Restaurants')
                        close()
                      }}
                      onClose={close}
                    >
                      {optionLabels.Restaurants}
                    </DropDownOption>
                    <DropDownOption
                      onSelect={async () => {
                        if (mode !== 'Catering') onDropdownChange('Catering')
                        close()
                      }}
                      onClose={close}
                    >
                      {optionLabels.Catering}
                    </DropDownOption>
                    {nvsMarketplaceLeadForms && (
                      <DropDownOption
                        onSelect={async () => {
                          if (mode !== 'Venues') onDropdownChange('Venues')
                          close()
                        }}
                        onClose={close}
                      >
                        {optionLabels.Venues}
                      </DropDownOption>
                    )}
                  </>
                )}
              </DropDown>
              <div className='location-dropdown-divider' />
            </>
          )}
          <div className='location-dropdown-input'>
            <LocationInput
              history={history}
              parsedQS={parsedQS}
              street={street}
              neighborhood={neighborhood}
              city={city}
              state={state}
              onLocationSelected={onLocationSelected}
              onDropdownChange={onDropdownChange}
              wrapperRef={wrapperRef}
            />
          </div>
        </div>
        <LocationSearchError />
      </div>
    </LocationSearchErrorContextProvider>
  )
}
export default LocationDropDown
